import { z } from "zod"
import { LiquidProductVariantSchema } from "../../models/shopify/liquid/variant"
import type { UVInsertFaceElement, UVInsertFaceElementConfigData } from "../../models/uv-inserts"
import { UVInsertConfigSchema } from "../../models/uv-inserts"
import { SuggestedMessagingAreaSchema } from "../../models/uv-inserts/suggested-messaging"
import { UVInsertsConfigDTO, UVInsertConfigDTO, UVInsertFaceElementDTO } from "./types"

type UVInsertConfigData = z.infer<typeof UVInsertConfigSchema>

const toFaceElement = (dto: UVInsertFaceElementDTO): UVInsertFaceElement => ({
  type: dto.type ?? "INSERT",
  location: dto.location ?? "FRONT",
  /** Zod refines DTO → {@link UVInsertFaceElementConfigData} in {@link UVInsertConfigSchema.parse}. */
  elementConfig: (dto.elementConfig ?? []) as UVInsertFaceElementConfigData[],
  subtitle: dto.subtitle,
  color: dto.color,
  backgroundColor: dto.backgroundColor,
})

const toConfig = (dto: UVInsertConfigDTO): UVInsertConfigData => {
  const themes = LiquidProductVariantSchema.array().parse(dto.themes ?? [])

  const suggestedMessagingArea = dto.suggestedMessagingArea
    ? SuggestedMessagingAreaSchema.parse(dto.suggestedMessagingArea)
    : undefined

  return UVInsertConfigSchema.parse({
    id: dto.id ?? "",
    handle: dto.handle ?? "",
    active: dto.active,
    themes,
    suggestedMessagingArea,
    title: dto.title,
    thumbnailSrc: dto.thumbnailSrc ?? null,
    description: dto.description,
    marketSegment: dto.marketSegment ?? [],
    addOnVariantIds: dto.addOnVariantIds,
    faceElements: (dto.faceElements ?? []).map((fe) => toFaceElement(fe)),
  })
}

export const parseUvInsertConfig = ({
  dto,
}: { dto?: UVInsertsConfigDTO }): UVInsertConfigData[] => {
  if (!dto?.configs) return []

  return dto.configs
    .filter((config): config is UVInsertConfigDTO => Boolean(config))
    .map(toConfig)
}
